import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { HaynesProDetailsFormState } from '@/hooks/useVehicleTechnicalData';

export interface HaynesProDetailsFormProps {
  value: HaynesProDetailsFormState;
  onChange: (next: HaynesProDetailsFormState) => void;
  onSearch: () => void;
  isSearching: boolean;
  disabled?: boolean;
}

export function HaynesProDetailsForm({
  value,
  onChange,
  onSearch,
  isSearching,
  disabled = false,
}: HaynesProDetailsFormProps) {
  const { t } = useTranslation('vehicles');
  const { t: tc } = useTranslation('common');

  const setField = (field: keyof HaynesProDetailsFormState, next: string) => {
    onChange({ ...value, [field]: next });
  };

  const canSearch = value.make.trim().length > 0 && value.model.trim().length > 0;

  return (
    <Box
      component="form"
      onSubmit={(event) => {
        event.preventDefault();
        if (canSearch && !isSearching && !disabled) onSearch();
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('technicalData.detailsFallbackHint')}
      </Typography>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            label={t('make')}
            value={value.make}
            onChange={(event) => setField('make', event.target.value)}
            required
            disabled={disabled}
            fullWidth
            slotProps={{
              htmlInput: { maxLength: 100, 'aria-required': true, 'aria-label': t('make') },
            }}
          />
          <TextField
            label={t('model')}
            value={value.model}
            onChange={(event) => setField('model', event.target.value)}
            required
            disabled={disabled}
            fullWidth
            slotProps={{
              htmlInput: { maxLength: 100, 'aria-required': true, 'aria-label': t('model') },
            }}
          />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            label={t('technicalData.engineCode')}
            value={value.engineCode}
            onChange={(event) => setField('engineCode', event.target.value)}
            disabled={disabled}
            fullWidth
            slotProps={{
              htmlInput: { maxLength: 32, 'aria-label': t('technicalData.engineCode') },
            }}
          />
          <TextField
            label={t('technicalData.engineCapacity')}
            value={value.engineCapacity}
            onChange={(event) => setField('engineCapacity', event.target.value)}
            type="number"
            disabled={disabled}
            fullWidth
            slotProps={{
              htmlInput: {
                min: 0,
                max: 20000,
                inputMode: 'numeric',
                'aria-label': t('technicalData.engineCapacity'),
              },
            }}
          />
          <TextField
            label={t('technicalData.engineOutput')}
            value={value.engineOutput}
            onChange={(event) => setField('engineOutput', event.target.value)}
            type="number"
            disabled={disabled}
            fullWidth
            slotProps={{
              htmlInput: {
                min: 0,
                max: 2000,
                inputMode: 'numeric',
                'aria-label': t('technicalData.engineOutput'),
              },
            }}
          />
        </Stack>
        <TextField
          label={t('technicalData.manufacturingYear')}
          value={value.manufacturingYear}
          onChange={(event) => setField('manufacturingYear', event.target.value)}
          type="number"
          disabled={disabled}
          helperText={t('technicalData.manufacturingYearHint')}
          slotProps={{
            htmlInput: {
              min: 1900,
              max: 2100,
              inputMode: 'numeric',
              'aria-label': t('technicalData.manufacturingYear'),
            },
          }}
        />
        <Box>
          <Button
            type="submit"
            variant="outlined"
            disabled={!canSearch || isSearching || disabled}
            aria-label={t('technicalData.searchByDetailsAria')}
          >
            {isSearching ? tc('loading') : t('technicalData.searchByDetails')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
